import i18next from "i18next";
import { z } from "zod";
import {
  FIELD_CAMPAIGN_MUST_HAVE_ONE_DAY_ACTIVE,
  FIELD_END_DATE_LESS_THAN_NOW,
  FIELD_MAX_LENGTH,
  FIELD_MIN_TOTAL_BUDGET_ONE_DOLLAR,
  FIELD_NUMBER_NEGATIVE,
  FIELD_REQUIRED,
  FIELD_URL_VALID_WITH_PROTOCOL,
  START_DATE_AND_END_DATE_VALIDATION,
} from "../../../../../i18n/keysTranslations";
import {
  ADS_CAMPAIGN_TYPE_BUDGET_LIFETIME_OPTION,
  ADS_CAMPAIGN_GOOGLE_SITE_LINK_NAME_MAX_LENGTH,
  ADS_CAMPAIGN_GOOGLE_SITE_LINK_DESCRIPTION_MAX_LENGTH,
  ADS_CAMPAIGN_GOOGLE_CALL_OUT_MAX_LENGTH,
  ADS_CAMPAIGN_TYPE_SEARCH_OPTION,
  ADS_CAMPAIGN_GOOGLE_BUSINESS_NAME_MAX_LENGTH,
  ADS_CAMPAIGN_TYPE_APP_OPTION,
} from "../../../../../utils/constants";
import { differenceInDays } from "date-fns";
import { REGEX_URL_WITH_PROTOCOL_REQUIRED } from "../../../../../utils/regex";
import isAfter from "date-fns/isAfter";
import isBefore from "date-fns/isBefore";
import startOfDay from "date-fns/startOfDay";

export const validateGoogleDsaSettings = ({
  path,
  data,
  ctx,
  hasDsaAdGroups,
}) => {
  if (!hasDsaAdGroups) return;
  const dsa = data?.dynamicSearchAdsSetting;
  if (!dsa?.domain || !dsa.domain.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...path, "dynamicSearchAdsSetting", "domain"],
      message: i18next.t(FIELD_REQUIRED),
    });
  }
  if (!dsa?.language || !dsa.language.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...path, "dynamicSearchAdsSetting", "language"],
      message: i18next.t(FIELD_REQUIRED),
    });
  }
};

const textItemSchema = z.object({
  id: z.string().optional(),
  value: z.string().optional(),
});

// Accepts any type (null/undefined included) and only reports "required",
// never zod's technical type messages.
const requiredAny = z.any().refine(
  (value) =>
    typeof value === "string" ? value.trim().length > 0 : Boolean(value),
  () => ({
    message: i18next.t(FIELD_REQUIRED),
  })
);

const googleBaseSchema = z.object({
  objective: requiredAny,
  integrationID: requiredAny,
  adAccountID: requiredAny,

  locations: z.array(z.any()).optional(),
  typeBudget: z.string().min(1, i18next.t(FIELD_REQUIRED)),
  budget: z.any(),
  businessName: z.any().optional(),
  type: z.any().optional(),
  siteLinks: z
    .array(
      z.object({
        linkText: z.string().optional(),
        finalUrl: z.string().optional(),
        descriptions: z
          .array(z.string().optional())
          .min(2, i18next.t(FIELD_REQUIRED)),
      })
    )
    .optional(),
  callOuts: z.array(textItemSchema).optional(),

  conversionActions: z.array(z.any()).optional(),
  startDate: z.any().optional(),
  endDate: z.any().optional(),
  logoUrl: z.any().optional(),
  imageUrls: z.any().optional(),
  networkSettings: z
    .object({
      targetGoogleSearch: z.boolean().optional(),
      targetSearchNetwork: z.boolean().optional(),
      targetContentNetwork: z.boolean().optional(),
    })
    .optional(),
  enableAIMax: z.any().optional(),
  biddingStrategy: z.any().optional(),
  brandGuidelinesEnabled: z.any().optional(),
  appStore: z.any().optional(),
  appID: z.any().optional(),
  dynamicSearchAdsSetting: z.any().optional(),
  kpiSettings: z.any().optional(),
});

export const googleCampaignSchema = googleBaseSchema.superRefine(
  (data, ctx) => {
    if (data.objective === ADS_CAMPAIGN_TYPE_SEARCH_OPTION) {
      if (!data.businessName) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["businessName"],
          message: i18next.t(FIELD_REQUIRED),
        });
      }

      if (
        data?.businessName?.length >
        ADS_CAMPAIGN_GOOGLE_BUSINESS_NAME_MAX_LENGTH
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["businessName"],
          message: i18next.t(FIELD_MAX_LENGTH, {
            value: ADS_CAMPAIGN_GOOGLE_BUSINESS_NAME_MAX_LENGTH,
          }),
        });
      }

      if (!data.networkSettings) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["networkSettings"],
          message: i18next.t(FIELD_REQUIRED),
        });
      }
    }

    if (data.objective === ADS_CAMPAIGN_TYPE_APP_OPTION) {
      if (!data.appStore) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["appStore"],
          message: i18next.t(FIELD_REQUIRED),
        });
      }
      if (!data.appID) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["appID"],
          message: i18next.t(FIELD_REQUIRED),
        });
      }
    }

    if (!data?.biddingStrategy?.type) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["biddingStrategy", "type"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }

    if (
      data.objective === ADS_CAMPAIGN_TYPE_APP_OPTION &&
      data?.biddingStrategy?.type === "TARGET_CPA"
    ) {
      const biddingValue = data?.biddingStrategy?.value;
      const numericBidding = Number(biddingValue);
      const isBiddingValueMissing =
        biddingValue === undefined ||
        biddingValue === null ||
        biddingValue === "" ||
        (typeof biddingValue === "string" &&
          String(biddingValue).trim() === "") ||
        Number.isNaN(numericBidding) ||
        numericBidding === 0;

      if (isBiddingValueMissing) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["biddingStrategy", "value"],
          message: i18next.t(FIELD_REQUIRED),
        });
      }
    }

    if (data?.biddingStrategy?.value && data?.biddingStrategy?.value < 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["biddingStrategy", "value"],
        message: i18next.t(FIELD_NUMBER_NEGATIVE),
      });
    }

    const startDate = data.startDate ? new Date(data.startDate) : null;
    const endDate = data.endDate ? new Date(data.endDate) : null;

    if (!startDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["startDate"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }

    // endDate es opcional: solo se valida si existe
    if (endDate && isBefore(endDate, startOfDay(new Date()))) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["endDate"],
        message: i18next.t(FIELD_END_DATE_LESS_THAN_NOW),
      });
    }

    if (startDate && endDate) {
      const differenceDays = differenceInDays(endDate, startDate);

      if (differenceDays <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["startDate"],
          message: i18next.t(FIELD_CAMPAIGN_MUST_HAVE_ONE_DAY_ACTIVE),
        });
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["endDate"],
          message: i18next.t(FIELD_CAMPAIGN_MUST_HAVE_ONE_DAY_ACTIVE),
        });
      }

      if (!isAfter(new Date(endDate), new Date(startDate))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["startDate"],
          message: i18next.t(START_DATE_AND_END_DATE_VALIDATION),
        });
      }
    }

    const numericBudget = Number(data.budget);
    if (isNaN(numericBudget) || !numericBudget) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["budget"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }

    const isLifetime =
      data.typeBudget === ADS_CAMPAIGN_TYPE_BUDGET_LIFETIME_OPTION;

    if (isLifetime) {
      const startDate = data.startDate ? new Date(data.startDate) : null;
      const endDate = data.endDate ? new Date(data.endDate) : null;

      if (startDate && endDate) {
        const diffDays = differenceInDays(endDate, startDate);

        if (diffDays > 0) {
          const dailyBudget = data.budget / diffDays;
          if (dailyBudget < 1.5) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["budget"],
              message: i18next.t(FIELD_MIN_TOTAL_BUDGET_ONE_DOLLAR, {
                value: diffDays,
              }),
            });
          }
        }
      }
    }

    if (!data.locations || data.locations.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["locations"],
        message: i18next.t(FIELD_REQUIRED),
      });
    }

    const showSiteLinksAndCallOuts =
      data.objective !== ADS_CAMPAIGN_TYPE_APP_OPTION;

    if (showSiteLinksAndCallOuts && data.callOuts?.length > 0) {
      data.callOuts.forEach((callOut, index) => {
        if (callOut.value.trim() === "") {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["callOuts", index, "value"],
            message: i18next.t(FIELD_REQUIRED),
          });
        }
        if (callOut.value.length > ADS_CAMPAIGN_GOOGLE_CALL_OUT_MAX_LENGTH) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["callOuts", index, "value"],
            message: i18next.t(FIELD_MAX_LENGTH, {
              value: ADS_CAMPAIGN_GOOGLE_CALL_OUT_MAX_LENGTH,
            }),
          });
        }
      });
    }

    if (showSiteLinksAndCallOuts && data.siteLinks?.length > 0) {
      data.siteLinks.forEach((siteLink, index) => {
        if (siteLink.linkText.trim() === "") {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["siteLinks", index, "linkText"],
            message: i18next.t(FIELD_REQUIRED),
          });
        }
        if (
          siteLink.linkText.length >
          ADS_CAMPAIGN_GOOGLE_SITE_LINK_NAME_MAX_LENGTH
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["siteLinks", index, "linkText"],
            message: i18next.t(FIELD_MAX_LENGTH, {
              value: ADS_CAMPAIGN_GOOGLE_SITE_LINK_NAME_MAX_LENGTH,
            }),
          });
        }
        if (siteLink.finalUrl.trim() === "") {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["siteLinks", index, "finalUrl"],
            message: i18next.t(FIELD_REQUIRED),
          });
        }
        if (
          !REGEX_URL_WITH_PROTOCOL_REQUIRED.test(encodeURI(siteLink.finalUrl))
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["siteLinks", index, "finalUrl"],
            message: i18next.t(FIELD_URL_VALID_WITH_PROTOCOL),
          });
        }
        if (siteLink.descriptions.length < 2) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["siteLinks", index, "descriptions"],
            message: i18next.t(FIELD_REQUIRED),
          });
        }
        siteLink.descriptions.forEach((description, descIndex) => {
          if (description.trim() === "") {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["siteLinks", index, "descriptions", descIndex],
              message: i18next.t(FIELD_REQUIRED),
            });
          }
          if (
            description.length >
            ADS_CAMPAIGN_GOOGLE_SITE_LINK_DESCRIPTION_MAX_LENGTH
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["siteLinks", index, "descriptions", descIndex],
              message: i18next.t(FIELD_MAX_LENGTH, {
                value: ADS_CAMPAIGN_GOOGLE_SITE_LINK_DESCRIPTION_MAX_LENGTH,
              }),
            });
          }
        });
      });
    }
  }
);
